import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/query'
import type { RootState, AppDispatch } from '../index'
import { API_BASE_URL } from '../../config/api'
import { setCredentials, logout } from '../slices/authSlice'

const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL

const realBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState
    const token = state.auth?.token ?? localStorage.getItem('token')
    if (token) headers.set('authorization', `Bearer ${token}`)
    return headers
  },
})

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>
) => {
  let result = await realBaseQuery(args, api, extraOptions)
  const requestUrl = typeof args === 'string' ? args : args.url
  if (result.error && result.error.status === 401 && !requestUrl?.includes('/auth/refresh')) {
    const state = api.getState() as RootState
    const token = state.auth?.token || localStorage.getItem('token')
    if (token) {
      try {
        const refreshResult = await fetch(`${baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        })
        if (refreshResult.ok) {
          const { access_token } = await refreshResult.json()
          ;(api.dispatch as AppDispatch)(setCredentials({ token: access_token, user: state.auth?.user ?? null }))
          result = await realBaseQuery(args, api, extraOptions)
        } else if (refreshResult.status === 401 || refreshResult.status === 403) {
          ;(api.dispatch as AppDispatch)(logout())
          if (window.location.pathname !== '/login') window.location.href = '/login'
        }
      } catch {
        // Dev/backend reload can temporarily drop /auth/refresh. Keep the token so the
        // current screen does not bounce to Login while the API comes back.
      }
    } else {
      ;(api.dispatch as AppDispatch)(logout())
      if (window.location.pathname !== '/login') window.location.href = '/login'
    }
  }
  return result
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'SaleContract', 'SaleContractPrefs', 'SaleContractColumnPrefs', 'SaleAmendment', 'SalePTBFFixation', 'PurchaseContractPrefs', 'FuturesControl', 'SaleControl', 'MatchingPs', 'SettingsRole', 'SettingsUser', 'SettingsLog', 'SettingsColumns', 'UserTableWidths', 'UserColumnVisibility', 'UserPageFilters', 'Preference'],
  endpoints: () => ({}),
})
